import { createMemo, createSignal, For, Show } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import { ChevronRight, Plus, School, ShieldCheck, Smartphone, Users } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { createPowerSyncQuery } from "@custodia/shared/solid";
import {
  type AulaRecord,
  filterProfilesByAula,
  getAulasQuery,
  getSupervisedProfilesQuery,
  queryKeys,
  type SupervisedProfileRecord,
} from "@custodia/shared";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { useNavigate } from "@tanstack/solid-router";
import { AulasPanel } from "./aulas-panel";
import { ProfileDialog } from "./profile-dialog";

export function SupervisionDashboard() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const navigate = useNavigate();
  const { userId } = session;

  const [dialogOpen, setDialogOpen] = createSignal(false);
  const [editing, setEditing] = createSignal<SupervisedProfileRecord | null>(null);
  /** Filtro por aula (id | "none" | null = todos) — multi-org escolar. */
  const [aulaFilter, setAulaFilter] = createSignal<string | null>(null);

  const profiles = createPowerSyncQuery<SupervisedProfileRecord>({
    queryKey: () => queryKeys.supervision.profiles(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getSupervisedProfilesQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const aulas = createPowerSyncQuery<AulaRecord>({
    queryKey: () => queryKeys.supervision.aulas(userId()),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getAulasQuery(dbInstance) : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: keepPreviousData,
    suspense: false,
  });

  const aulaName = (id: string | null) =>
    id ? (aulas.data?.find((a) => a.id === id)?.name ?? null) : null;

  const visibleProfiles = createMemo(() => filterProfilesByAula(profiles.data ?? [], aulaFilter()));

  const openNew = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openProfile = (profile: SupervisedProfileRecord) => {
    void navigate({ to: "/supervised/$profileId", params: { profileId: profile.id } });
  };

  return (
    <PageContainer maxWidth="4xl" class="overflow-y-auto">
      <div class="flex items-center justify-between gap-4 mb-6">
        <PageHeader
          title={t(msg`Supervised profiles`)}
          description={t(msg`Screen time, limits and transparency for the devices you supervise`)}
          icon={ShieldCheck}
        />
        <Button onClick={openNew} class="gap-2 shrink-0" data-testid="new-profile">
          <Plus class="h-4 w-4" />
          {t(msg`New profile`)}
        </Button>
      </div>

      <AulasPanel
        profiles={profiles.data ?? []}
        aulaFilter={aulaFilter()}
        onAulaFilterChange={setAulaFilter}
      />

      <Show
        when={!profiles.isLoading}
        fallback={
          <div class="space-y-3">
            <Skeleton class="h-20 w-full rounded-xl" />
            <Skeleton class="h-20 w-full rounded-xl" />
          </div>
        }
      >
        <Show
          when={(visibleProfiles().length ?? 0) > 0}
          fallback={
            <EmptyState
              icon={ShieldCheck}
              title={
                aulaFilter()
                  ? t(msg`No profiles in this classroom`)
                  : t(msg`No supervised profiles yet`)
              }
              description={
                aulaFilter()
                  ? t(msg`Assign profiles to this classroom from their edit dialog.`)
                  : t(
                      msg`Create a profile for a child or a device to set limits and see activity — always with a transparency notice they can see.`,
                    )
              }
            />
          }
        >
          <div class="grid gap-3" data-testid="profiles-list">
            <For each={visibleProfiles()}>
              {(profile) => (
                <button
                  type="button"
                  class="text-left"
                  onClick={() => openProfile(profile)}
                  data-testid="profile-row"
                >
                  <Card class="transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-[var(--surface-shadow)]">
                    <CardContent class="flex items-center gap-4 p-4">
                      <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <ShieldCheck class="h-6 w-6" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <h3 class="font-semibold truncate">{profile.name}</h3>
                          <Show when={profile.organization_id}>
                            <Badge variant="secondary" class="gap-1 shrink-0">
                              <Users class="h-3 w-3" />
                              {t(msg`Team`)}
                            </Badge>
                          </Show>
                          <Show when={aulaName(profile.aula_id)}>
                            {(name) => (
                              <Badge
                                variant="outline"
                                class="gap-1 shrink-0"
                                data-testid="profile-aula-badge"
                              >
                                <School class="h-3 w-3" />
                                {name()}
                              </Badge>
                            )}
                          </Show>
                        </div>
                        <Show when={profile.device_label}>
                          <p class="flex items-center gap-1 text-sm text-muted-foreground truncate">
                            <Smartphone class="h-3 w-3 shrink-0" />
                            {profile.device_label}
                          </p>
                        </Show>
                      </div>
                      <ChevronRight class="h-5 w-5 shrink-0 text-muted-foreground" />
                    </CardContent>
                  </Card>
                </button>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <ProfileDialog open={dialogOpen()} onOpenChange={setDialogOpen} profile={editing()} />
    </PageContainer>
  );
}
